import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { SmartRecommendation } from '../services/smartRecommendationService';

interface SmartRecommendationPopupProps {
    recommendation: SmartRecommendation;
    onAction: (action: string) => void;
    onDismiss: () => void;
    isDarkMode: boolean;
}

const SmartRecommendationPopup: React.FC<SmartRecommendationPopupProps> = ({
    recommendation,
    onAction,
    onDismiss,
    isDarkMode
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [progress, setProgress] = useState(100);

    // Auto-dismiss timer
    const autoExpiry = recommendation.expiresAt
        ? recommendation.expiresAt - Date.now()
        : 20000; // Default 20 seconds

    useEffect(() => {
        // Animate in
        const showTimer = setTimeout(() => setIsVisible(true), 50);

        // Progress bar countdown
        const startTime = Date.now();
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 100 - (elapsed / autoExpiry) * 100);
            setProgress(remaining);
            if (remaining <= 0) {
                handleDismiss();
            }
        }, 100);

        return () => {
            clearTimeout(showTimer);
            clearInterval(interval);
        };
    }, [autoExpiry]);

    const handleDismiss = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => {
            onDismiss();
        }, 300);
    }, [onDismiss]);

    const handleAction = useCallback((action: string) => {
        if (action === 'dismiss') {
            handleDismiss();
            return;
        }
        setIsExiting(true);
        setTimeout(() => {
            onAction(action);
        }, 200);
    }, [onAction, handleDismiss]);

    // Priority-based accent colors
    const accentColors = {
        high: {
            border: 'border-amber-500/30',
            bg: 'from-amber-500/10 to-amber-600/5',
            progress: 'bg-amber-500',
            primaryBtn: 'bg-amber-500 hover:bg-amber-400 text-slate-900',
            glow: 'shadow-amber-500/20'
        },
        medium: {
            border: 'border-indigo-500/30',
            bg: 'from-indigo-500/10 to-indigo-600/5',
            progress: 'bg-indigo-500',
            primaryBtn: 'bg-indigo-500 hover:bg-indigo-400 text-white',
            glow: 'shadow-indigo-500/20'
        },
        low: {
            border: 'border-slate-500/30',
            bg: 'from-slate-500/10 to-slate-600/5',
            progress: 'bg-slate-400',
            primaryBtn: 'bg-slate-600 hover:bg-slate-500 text-white',
            glow: 'shadow-slate-500/10'
        }
    };

    const colors = accentColors[recommendation.priority];

    return (
        <div
            className={`fixed bottom-24 right-4 z-[60] w-[340px] max-w-[calc(100vw-2rem)] transition-all duration-300 ease-out ${
                isVisible && !isExiting
                    ? 'translate-y-0 opacity-100 scale-100'
                    : 'translate-y-4 opacity-0 scale-95'
            }`}
        >
            <div className={`relative overflow-hidden rounded-2xl border ${colors.border} ${
                isDarkMode
                    ? 'bg-slate-900/95 backdrop-blur-xl'
                    : 'bg-white/95 backdrop-blur-xl'
            } shadow-2xl ${colors.glow}`}>

                {/* Progress bar (auto-dismiss countdown) */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-slate-200/20">
                    <div
                        className={`h-full ${colors.progress} transition-all duration-100 ease-linear`}
                        style={{ width: `${progress}%` }}
                    />
                </div>

                {/* Dismiss button */}
                {recommendation.dismissable && (
                    <button
                        onClick={handleDismiss}
                        className={`absolute top-3 right-3 p-1 rounded-full transition-colors z-10 ${
                            isDarkMode
                                ? 'hover:bg-slate-700 text-slate-500 hover:text-slate-300'
                                : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
                        }`}
                    >
                        <X size={14} />
                    </button>
                )}

                {/* Content */}
                <div className="p-4 pr-8">
                    {/* Icon + Title */}
                    <div className="flex items-start gap-3 mb-2">
                        <span className="text-2xl flex-shrink-0 mt-0.5">{recommendation.icon}</span>
                        <div className="min-w-0">
                            <h4 className={`text-sm font-bold leading-tight ${
                                isDarkMode ? 'text-white' : 'text-slate-900'
                            }`}>
                                {recommendation.title}
                            </h4>
                        </div>
                    </div>

                    {/* Description */}
                    <p className={`text-xs leading-relaxed mb-3 ml-9 ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                    }`}>
                        {recommendation.description}
                    </p>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-9">
                        <button
                            onClick={() => handleAction(recommendation.primaryAction.action)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95 ${colors.primaryBtn}`}
                        >
                            {recommendation.primaryAction.label}
                        </button>
                        {recommendation.secondaryAction && (
                            <button
                                onClick={() => handleAction(recommendation.secondaryAction!.action)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    isDarkMode
                                        ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                }`}
                            >
                                {recommendation.secondaryAction.label}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SmartRecommendationPopup;
